import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BarChartIcon,
  FileIcon,
  UsersIcon,
  MapIcon,
  SettingsIcon,
  CloseIcon
} from '../icons/Icons';

interface SidebarItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  roles: ('citizen' | 'official')[];
}

interface ResponsiveSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  userRole: 'citizen' | 'official';
  currentPage: string;
}

const ResponsiveSidebar: React.FC<ResponsiveSidebarProps> = ({ 
  isOpen, 
  onClose, 
  userRole,
  currentPage 
}) => {
  const navigate = useNavigate();
  
  const sidebarItems: SidebarItem[] = [
    // Citizen items
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: BarChartIcon,
      path: '/dashboard',
      roles: ['citizen']
    },
    {
      id: 'my-issues',
      label: 'My Issues',
      icon: FileIcon,
      path: '/my-issues',
      roles: ['citizen']
    },
    {
      id: 'browse',
      label: 'Browse Area Issues',
      icon: MapIcon,
      path: '/browse',
      roles: ['citizen']
    },
    {
      id: 'profile',
      label: 'Profile',
      icon: FileIcon,
      path: '/profile',
      roles: ['citizen']
    },
    // Official items
    {
      id: 'admin-dashboard',
      label: 'Dashboard',
      icon: BarChartIcon,
      path: '/admin',
      roles: ['official']
    },
    {
      id: 'assigned-issues',
      label: 'Assigned Issues',
      icon: FileIcon,
      path: '/admin/issues',
      roles: ['official']
    },
    {
      id: 'analytics',
      label: 'Analytics',
      icon: BarChartIcon,
      path: '/analytics',
      roles: ['official']
    },
    {
      id: 'manage-citizens',
      label: 'Manage Citizens',
      icon: UsersIcon,
      path: '/admin/citizens',
      roles: ['official']
    },
    {
      id: 'map-view',
      label: 'Map View',
      icon: MapIcon,
      path: '/admin/map',
      roles: ['official']
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: SettingsIcon,
      path: '/admin/settings',
      roles: ['official']
    }
  ];

  const filteredItems = sidebarItems.filter(item => item.roles.includes(userRole));

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };

  return (
    <>
      {/* Sidebar */}
      <aside className={`${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 fixed lg:relative z-[9999] w-64 h-screen bg-white shadow-lg transition-transform duration-300 ease-in-out top-0 left-0`}>
        <div className="flex justify-between items-center p-4 border-b border-gray-200 lg:hidden">
          <h2 className="text-lg font-semibold text-gray-800">Menu</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-md text-gray-600 hover:bg-gray-100"
          >
            <CloseIcon className="w-5 h-5" />
          </button>
        </div>
        <nav className="p-4 space-y-2 overflow-y-auto h-[calc(100vh-4rem)] lg:h-[calc(100vh-4rem)]">
          {filteredItems.map((item) => {
            const IconComponent = item.icon;
            const isActive = currentPage === item.id;
            
            return (
              <button 
                key={item.id}
                onClick={() => handleNavigation(item.path)}
                className={`w-full flex items-center space-x-3 px-4 py-3.5 text-left rounded-lg transition-colors ${
                  isActive 
                    ? (userRole === 'citizen' 
                        ? 'bg-blue-50 text-blue-700' 
                        : 'bg-green-50 text-green-700')
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <IconComponent className="w-5 h-5 flex-shrink-0" />
                <span className="truncate text-sm">{item.label}</span>
              </button>
            );
          })}
        </nav>
      </aside>

      {/* Overlay for mobile */}
      {isOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-[9998]"
          onClick={onClose}
        />
      )}
    </>
  );
};

export default ResponsiveSidebar;